import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Bell, BellRinging, CheckCircle, Truck, CookingPot, Seal, Package, Warning } from '@phosphor-icons/react';
import { useNotifications } from '../context/NotificationContext';

const iconMap = {
  'check-circle': CheckCircle,
  'truck': Truck,
  'cooking-pot': CookingPot,
  'seal': Seal,
  'package': Package,
  'warning': Warning,
};

const NotificationBell = ({ primaryColor = '#c2185b', standalone = true }) => {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleNotifClick = (notif) => {
    if (!notif.read) markAsRead(notif.id);
    setOpen(false);
  };

  const getLink = (notif) => {
    if (notif.tracking_code) return `/suivi/${notif.tracking_code}`;
    return standalone ? '/account/orders' : `/account/orders`;
  };

  return (
    <div className="relative" ref={ref} data-testid="notification-bell">
      <button
        onClick={() => setOpen(prev => !prev)}
        className="relative p-2 rounded-full hover:bg-white/10 transition-colors"
        data-testid="notification-bell-btn"
      >
        {unreadCount > 0 ? (
          <BellRinging size={20} weight="fill" className="text-white animate-bounce" />
        ) : (
          <Bell size={20} className="text-white/70" />
        )}
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full text-[10px] font-bold text-white flex items-center justify-center px-1"
            style={{ backgroundColor: primaryColor }}
            data-testid="notification-badge"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-[340px] bg-white rounded-2xl shadow-2xl border border-slate-100 z-[9999] overflow-hidden" data-testid="notification-dropdown">
          {/* Header */}
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-sm font-bold text-slate-800">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-[11px] font-medium hover:underline"
                style={{ color: primaryColor }}
                data-testid="mark-all-read-btn"
              >
                Tout marquer lu
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-[360px] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="py-10 text-center">
                <Bell size={28} className="mx-auto text-slate-200 mb-2" />
                <p className="text-slate-400 text-xs">Aucune notification</p>
              </div>
            ) : (
              notifications.map((notif) => {
                const Icon = iconMap[notif.icon] || Package;
                return (
                  <Link
                    key={notif.id}
                    to={getLink(notif)}
                    onClick={() => handleNotifClick(notif)}
                    className={`flex items-start gap-3 px-4 py-3 hover:bg-slate-50 transition-colors border-b border-slate-50 ${!notif.read ? 'bg-blue-50/40' : ''}`}
                    data-testid={`notification-item-${notif.id}`}
                  >
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                      style={{ backgroundColor: !notif.read ? primaryColor : '#e2e8f0', color: !notif.read ? '#fff' : '#94a3b8' }}
                    >
                      <Icon size={14} weight="fill" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`text-[13px] ${!notif.read ? 'font-semibold text-slate-900' : 'text-slate-600'}`}>
                          {notif.title}
                        </p>
                        {!notif.read && <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: primaryColor }} />}
                      </div>
                      <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{notif.message}</p>
                      <p className="text-[10px] text-slate-400 mt-1">
                        {new Date(notif.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </Link>
                );
              })
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-2.5 border-t border-slate-100 text-center">
              <Link
                to={standalone ? '/account/orders' : '/account/orders'}
                onClick={() => setOpen(false)}
                className="text-[11px] font-medium hover:underline"
                style={{ color: primaryColor }}
              >
                Voir toutes mes commandes
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
